import { useMutation } from '@apollo/client';
import React from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Stack,
  Switch,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

export const tenantIsRealFragment = graphql(`
  fragment TenantIsReal on AdminTenants @_unmask {
    __typename
    id
    isRealTenant
  }
`);

const setTenantIsRealMutation = graphql(
  `
    mutation setTenantIsReal($tenantId: uuid!, $isRealTenant: Boolean!) {
      updateAdminTenants(
        where: { id: { _eq: $tenantId } }
        _set: { isRealTenant: $isRealTenant }
      ) {
        returning {
          ...TenantIsReal
        }
      }
    }
  `,
  [tenantIsRealFragment],
);

interface TenantIsRealToggleProps {
  tenantId: string;
}

export const TenantIsRealToggle: React.FC<TenantIsRealToggleProps> = ({
  tenantId,
}) => {
  const toast = useToast();

  const tenant = useCompleteFragment({
    fragment: tenantIsRealFragment,
    key: {
      id: tenantId,
    },
  });

  const [updateIsReal] = useMutation(setTenantIsRealMutation, {
    onError: () => toast.error('Failed to update real tenant status'),
    optimisticResponse: (data) => ({
      updateAdminTenants: {
        returning: [
          {
            __typename: 'AdminTenants' as const,
            id: tenantId,
            isRealTenant: data.isRealTenant,
          },
        ],
      },
    }),
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Stack gap={2} fullWidth={false} direction="horizontal">
          <Text inline size="xs" color="secondary">
            Real Tenant
          </Text>
          <Switch
            size="sm"
            checked={tenant.isRealTenant ?? true}
            aria-label="Real Tenant"
            onCheckedChange={(checked) => {
              updateIsReal({
                variables: {
                  tenantId,
                  isRealTenant: checked,
                },
              });
            }}
          />
        </Stack>
      </TooltipTrigger>
      <TooltipContent>
        Real tenants are customer organizations. Admins are auto-deprovisioned
        from real tenants. Internal/test tenants are excluded from reports.
      </TooltipContent>
    </Tooltip>
  );
};
